import { useEffect, useRef, useState } from 'react'
import styles from './WhatThatFeelsLike.module.css'

// Words that float in the background — the qualities of good edits
const floatingWords = [
  { text: 'intentional', x: 12, y: 18, size: 1.1, depth: 0.6 },
  { text: 'precise', x: 68, y: 12, size: 1.3, depth: 0.4 },
  { text: 'coherent', x: 82, y: 42, size: 0.9, depth: 0.7 },
  { text: 'logical', x: 25, y: 72, size: 1.0, depth: 0.5 },
  { text: 'considered', x: 55, y: 80, size: 1.2, depth: 0.3 },
  { text: 'quiet', x: 88, y: 70, size: 0.85, depth: 0.8 },
  { text: 'right', x: 40, y: 22, size: 1.5, depth: 0.2 },
  { text: 'fit', x: 8, y: 55, size: 0.9, depth: 0.9 },
  { text: 'complete', x: 72, y: 58, size: 1.0, depth: 0.6 },
  { text: 'native', x: 45, y: 55, size: 0.8, depth: 0.7 },
  { text: 'clear', x: 30, y: 40, size: 1.1, depth: 0.5 },
]

const TechnologyShowcase = () => {
  const sectionRef = useRef<HTMLElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [mouse, setMouse] = useState({ x: 50, y: 50 })
  const [scrollProgress, setScrollProgress] = useState(0)
  const [entered, setEntered] = useState(false)

  // Mouse parallax for the floating words
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      setMouse({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  // Scroll entrance animation
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return
      const rect = sectionRef.current.getBoundingClientRect()
      const progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight * 0.6)))
      setScrollProgress(progress)
      if (progress > 0.1) setEntered(true)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <section ref={sectionRef} className={styles.section}>
      {/* Deep radial glow */}
      <div className={styles.deepGlow} />

      <div ref={containerRef} className={styles.stage}>
        {/* Floating background words */}
        {floatingWords.map((word, i) => {
          const offsetX = (mouse.x - word.x) * word.depth * -0.08
          const offsetY = (mouse.y - word.y) * word.depth * -0.08
          return (
            <span
              key={i}
              className={styles.floatWord}
              style={{
                left: `${word.x}%`,
                top: `${word.y}%`,
                fontSize: `${word.size * 14}px`,
                opacity: entered ? word.depth * 0.18 : 0,
                transform: `translate(-50%, -50%) translate(${offsetX}px, ${offsetY}px)`,
                transition: `opacity 1.2s ease ${i * 0.08}s, transform 0.4s ease`,
                transitionDelay: entered ? `${i * 0.06}s` : '0s',
              }}
            >
              {word.text}
            </span>
          )
        })}

        {/* Main content */}
        <div className="container">
          <div className={styles.layout}>

            <div className={styles.labelColumn}>
              <div className={styles.sectionTag}>
                <span className={styles.tagNumber}>03</span>
                <span className={styles.tagLine} />
                <span className={styles.tagText}>The Feeling</span>
              </div>
            </div>

            <div className={styles.contentColumn}>

              {/* Large centrepiece statement */}
              <div
                className={styles.centrepiece}
                style={{
                  opacity: 0.1 + scrollProgress * 0.9,
                  transform: `translateY(${(1 - scrollProgress) * 40}px)`,
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                }}
              >
                <p className={styles.largeLine}>What does it feel like</p>
                <p className={styles.largeLine}>
                  when the help is actually{' '}
                  <em className={styles.largeEmphasis}>helpful?</em>
                </p>
              </div>

              {/* Supporting paragraphs */}
              <div className={styles.supportingText}>
                <p
                  className={styles.supportPara}
                  style={{
                    opacity: 0.05 + scrollProgress * 0.85,
                    transform: `translateY(${(1 - scrollProgress) * 20}px)`,
                    transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
                  }}
                >
                  The best edits are the ones you don't have to think about. It isn't that they're automatic. It's that they're{' '}
                  <span className={styles.highlight}>correct.</span> They fit the logic
                  of what you are already building. They close the gap between what
                  you meant and what the file says.
                </p>

                <p
                  className={styles.finalStatement}
                  style={{
                    opacity: 0.05 + scrollProgress * 0.85,
                    transform: `translateY(${(1 - scrollProgress) * 20}px)`,
                    transition: 'opacity 0.8s ease 0.28s, transform 0.8s ease 0.28s',
                  }}
                >
                  That clarity, accepting an edit without a second thought, is
                  what Box Editor is built to deliver.
                  <br />
                  <br />
                  <span className={styles.buildLine}>That's what AI-assisted coding should feel like.</span>
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TechnologyShowcase
